/**
 * Login page component for Crossover authentication
 * Handles user login and authentication state management
 */

import React, { useState } from 'react';
import { login, getUserProfile, AuthError } from '../services/authService';
import { AuthState } from '../types';

interface LoginPageProps {
  onAuthSuccess: (authState: AuthState) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onAuthSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle form submission and authentication
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Authenticate and get token
      console.log('Attempting login...');
      const authResponse = await login(username.trim(), password);
      
      // Step 2: Get user profile and manager ID
      console.log('Getting user profile...');
      const userProfile = await getUserProfile(authResponse.token);
      
      // Step 3: Create auth state
      const authState: AuthState = {
        isAuthenticated: true,
        token: authResponse.token,
        managerId: userProfile.managerAvatar?.id || null,
        userProfile,
        teamMembership: null,
        loading: false,
        error: null
      };

      console.log('Authentication successful:', {
        managerId: authState.managerId,
        userName: userProfile.printableName
      });

      // Step 4: Notify parent component
      onAuthSuccess(authState);
      
    } catch (err) {
      console.error('Authentication failed:', err);
      
      if (err instanceof AuthError) {
        switch (err.statusCode) {
          case 401:
            setError('Invalid username or password. Please check your credentials.');
            break;
          case 403:
            setError('Access forbidden. Please contact your administrator.');
            break;
          default:
            setError(err.message || 'Authentication failed. Please try again.');
        }
      } else {
        setError('Network error. Please check your internet connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear error when user starts typing
   */
  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      if (error) {
        setError(null);
      }
    };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h2>Login Required</h2>
          <p>Sign in with your Crossover credentials to access the AI leaderboard</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">
              Username/Email
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={handleInputChange(setUsername)}
              disabled={loading}
              placeholder="Enter your Crossover username or email"
              autoComplete="username"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={handleInputChange(setPassword)}
              disabled={loading}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <div className="status-message error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !username.trim() || !password.trim()}
            className="login-button"
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="login-footer">
          <p className="login-note">
            <strong>Note:</strong> Basic view is available to all users. My Team view shows your team members. Central PS view is available if you're a member of Central PS teams.
          </p>
          <p className="login-security">
            Your credentials are securely transmitted and not stored on this device.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 